import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Hand edit of one widget. Mirrors the backend spec
// (app/models/dashboards/spec.py); the server validates again on save.

// Same names as SOURCE_TOOLS in app/models/dashboards/spec.py.
export const SOURCE_TOOLS = [
  { value: 'jira_search_issues', label: 'Jira issues' },
  { value: 'get_linear_issues', label: 'Linear issues' },
];
export const WIDGET_TYPES = ['kpi', 'bar', 'line', 'pie', 'table', 'text'];
const TRANSFORMS = [
  { value: 'count', label: 'Count rows' },
  { value: 'group_by', label: 'Count per value of a field' },
  { value: 'sum', label: 'Sum of a field' },
  { value: 'avg', label: 'Average of a field' },
  { value: 'timeseries', label: 'Count per day (date field)' },
  { value: 'none', label: 'Rows as they are (tables)' },
];
const NEEDS_FIELD = new Set(['group_by', 'sum', 'avg', 'timeseries']);
const TAKES_BY = new Set(['count', 'sum', 'avg']);
const ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
// Tile sizes on the 12-column grid; the keyboard way to resize a tile.
export const SIZES = [
  { label: 'Small', w: 3, h: 2 },
  { label: 'Medium', w: 6, h: 4 },
  { label: 'Wide', w: 8, h: 4 },
  { label: 'Full width', w: 12, h: 5 },
];

const blankSource = () => ({
  tool: SOURCE_TOOLS[0].value,
  args: { context: 'jql', jql: '' },
  transform: 'count',
});

/** Form state -> widget, or a list of problems. */
export const toWidget = (form, takenIds) => {
  const problems = [];
  if (!ID_PATTERN.test(form.id))
    problems.push('Id: letters, digits, - and _ only.');
  else if (takenIds.has(form.id)) problems.push('Id is already used.');
  if (!form.title.trim()) problems.push('Title is required.');

  const widget = { id: form.id, type: form.type, title: form.title.trim() };
  if (form.type === 'text') {
    if (!form.text.trim()) problems.push('Text is required.');
    widget.text = form.text;
    return problems.length ? { problems } : { widget };
  }

  let args;
  try {
    args = form.argsText.trim() ? JSON.parse(form.argsText) : {};
    if (!args || typeof args !== 'object' || Array.isArray(args))
      throw new Error();
  } catch {
    problems.push('Query arguments must be a JSON object.');
  }
  if (NEEDS_FIELD.has(form.transform) && !form.field.trim())
    problems.push('This calculation needs a field.');

  widget.source = { tool: form.tool, args, transform: form.transform };
  if (NEEDS_FIELD.has(form.transform)) widget.source.field = form.field.trim();
  if (TAKES_BY.has(form.transform) && form.by.trim())
    widget.source.by = form.by.trim();
  // Chart hints are not edited here; keep whatever the agent set.
  if (form.encoding && Object.keys(form.encoding).length)
    widget.encoding = form.encoding;
  return problems.length ? { problems } : { widget };
};

const toForm = (widget) => {
  const source = widget.source || blankSource();
  return {
    id: widget.id,
    type: widget.type,
    title: widget.title || '',
    text: widget.text || '',
    tool: source.tool,
    argsText: JSON.stringify(source.args || {}, null, 2),
    transform: source.transform || 'count',
    field: source.field || '',
    by: source.by || '',
    encoding: widget.encoding || null,
  };
};

const Label = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="mb-1 block text-[11px] font-medium text-zinc-400"
  >
    {children}
  </label>
);
const inputClass =
  'w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-[#5652CF] focus:outline-none';

/** `widget` null = new widget. `takenIds`: ids of the other widgets.
 * `size`: the tile's current { w, h }. `onSave(widget, size)`. */
const WidgetEditor = ({ widget, takenIds, size, onSave, onClose }) => {
  const isNew = !widget;
  const [form, setForm] = useState(() =>
    toForm(
      widget || {
        id: `w${Date.now().toString(36)}`,
        type: 'kpi',
        title: '',
        source: blankSource(),
      },
    ),
  );
  const [problems, setProblems] = useState([]);
  const [tileSize, setTileSize] = useState(
    () => size || (widget ? null : SIZES[1]),
  );

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const result = toWidget(form, takenIds);
    if (result.problems) setProblems(result.problems);
    else onSave(result.widget, tileSize);
  };

  const isText = form.type === 'text';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <form
        role="dialog"
        aria-label={isNew ? 'Add widget' : 'Edit widget'}
        onSubmit={submit}
        className="flex max-h-[90vh] w-full max-w-lg flex-col gap-3 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 p-4 text-zinc-200 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            {isNew ? 'Add widget' : 'Edit widget'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="p-1 text-zinc-400"
          >
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="w-title">Title</Label>
            <input
              id="w-title"
              value={form.title}
              onChange={set('title')}
              className={inputClass}
            />
          </div>
          <div>
            <Label htmlFor="w-type">Type</Label>
            <select
              id="w-type"
              value={form.type}
              onChange={set('type')}
              className={inputClass}
            >
              {WIDGET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {isNew && (
            <div className="sm:col-span-2">
              <Label htmlFor="w-id">Id</Label>
              <input
                id="w-id"
                value={form.id}
                onChange={set('id')}
                className={inputClass}
              />
            </div>
          )}
        </div>

        {isText ? (
          <div>
            <Label htmlFor="w-text">Text (markdown)</Label>
            <textarea
              id="w-text"
              rows={6}
              value={form.text}
              onChange={set('text')}
              className={inputClass}
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="w-tool">Data</Label>
                <select
                  id="w-tool"
                  value={form.tool}
                  onChange={set('tool')}
                  className={inputClass}
                >
                  {SOURCE_TOOLS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="w-transform">Calculation</Label>
                <select
                  id="w-transform"
                  value={form.transform}
                  onChange={set('transform')}
                  className={inputClass}
                >
                  {TRANSFORMS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {NEEDS_FIELD.has(form.transform) && (
                <div>
                  <Label htmlFor="w-field">Field</Label>
                  <input
                    id="w-field"
                    placeholder="status, created, customfield_10016…"
                    value={form.field}
                    onChange={set('field')}
                    className={inputClass}
                  />
                </div>
              )}
              {TAKES_BY.has(form.transform) && (
                <div>
                  <Label htmlFor="w-by">Split by (optional)</Label>
                  <input
                    id="w-by"
                    placeholder="assignee, priority…"
                    value={form.by}
                    onChange={set('by')}
                    className={inputClass}
                  />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="w-args">Query arguments (JSON)</Label>
              <textarea
                id="w-args"
                rows={4}
                spellCheck={false}
                value={form.argsText}
                onChange={set('argsText')}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-[11px] text-zinc-500">
                Jira: {'{"context": "jql", "jql": "project = OD AND …"}'} — the
                JQL must name a project.
              </p>
            </div>
          </>
        )}

        <fieldset>
          <legend className="mb-1 block text-[11px] font-medium text-zinc-400">
            Size
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {SIZES.map((option) => {
              const active =
                tileSize?.w === option.w && tileSize?.h === option.h;
              return (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTileSize({ w: option.w, h: option.h })}
                  className={`rounded-lg border px-3 py-1.5 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#5652CF] ${
                    active
                      ? 'border-[#5652CF] bg-[#5652CF]/20 text-zinc-100'
                      : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        {problems.length > 0 && (
          <ul role="alert" className="list-disc pl-5 text-xs text-amber-300">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-lg bg-[#5652CF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            {isNew ? 'Add' : 'Apply'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default WidgetEditor;
